import { formatCurrency, formatMoney, formatNumber, pickNumber } from '../utils/helpers';
import { getPopularProductsSalesColumns } from '../utils/constants';

const parsePopularProducts = (aggs = {}, opts = {}) => {
  const { topN = 10, currency = '', includeAllItems = false } = opts;

  if (!aggs) {
    return {
      charts: {
        itemsByCount: { labels: [], values: [] },
        itemsByProfit: { labels: [], values: [] },
        categoriesByCount: { labels: [], values: [] },
        categoriesByProfit: { labels: [], values: [] }
      },
      tableRows: [],
      tableRowsRaw: [],
      summary: {
        totalQuantity: 0,
        totalValue: 0,
        totalCost: 0,
        totalProfit: 0,
        totalQuantityStr: '0',
        totalValueStr: formatCurrency(0, currency),
        totalCostStr: formatCurrency(0, currency),
        totalProfitStr: formatCurrency(0, currency),
        currency
      },
      mainColumns: getPopularProductsSalesColumns(),
    };
  }

  const itemsByCountBuckets = aggs.popular_items_by_count?.buckets || [];
  const itemsByProfitBuckets = aggs.popular_items_by_profit?.buckets || [];
  const categoriesByCountBuckets = aggs.popular_categories_by_count?.buckets || [];
  const categoriesByProfitBuckets = aggs.popular_categories_by_profit?.buckets || [];

  const totalQuantity = pickNumber(aggs.total_sales_count || {}, ['value']);
  const totalValue = pickNumber(aggs.total_amount || {}, ['value']);
  const totalCost = pickNumber(aggs.total_cost || {}, ['value']);
  const totalProfit = pickNumber(aggs.total_profit || {}, ['value']);

  const extractSingle = (aggObj, pathField) => {
    if (!aggObj) return '';
    if (Array.isArray(aggObj.buckets) && aggObj.buckets.length) return aggObj.buckets[0].key || '';
    return '';
  };

  const tableRowsRaw = itemsByCountBuckets.map((b) => {
    const quantity = pickNumber(b, ['sales_count']) || 0;
    const value = pickNumber(b, ['amount_sum']) || 0;
    const cost = pickNumber(b, ['cost_sum']) || 0;
    const profit = pickNumber(b, ['profit']) || (value - cost);
    const productName = extractSingle(b.product_name);
    const brand = extractSingle(b.brand);
    const categoryName = b.category?.buckets?.[0]?.key || '';
    const parentCategoryName = b.parentCategory?.buckets?.[0]?.key || '';
    return {
      productName,
      brand,
      categoryName,
      parentCategoryName,
      category: categoryName,
      quantity,
      value: formatMoney(value, currency),
      cost: formatMoney(cost, currency),
      profit: formatMoney(profit, currency),
    };
  });

  const tableRows = tableRowsRaw.map(r => [
    r.productName,
    r.brand || '',
    { type: 'category', name: r.categoryName || '', parent: r.parentCategoryName || '' },
    formatNumber(r.quantity),
    r.value,
    r.cost,
    r.profit,
  ]);

  const topItemsByCount = itemsByCountBuckets.slice(0, topN).map(b => {
    const label =
      (b.product_name && b.product_name.buckets && b.product_name.buckets[0] && b.product_name.buckets[0].key)
      || String(b.key || '');
    const value =
      (b.sales_count && typeof b.sales_count.value === 'number') ? b.sales_count.value
        : (typeof b.doc_count === 'number' ? b.doc_count : 0);
    return { label, value };
  });

  const topItemsByProfit = itemsByProfitBuckets.slice(0, topN).map(b => {
    const label =
      (b.product_name && b.product_name.buckets && b.product_name.buckets[0] && b.product_name.buckets[0].key)
      || String(b.key || '');
    const value =
      (b.profit && typeof b.profit.value === 'number') ? b.profit.value
        : pickNumber(b, ['profit']) || 0;
    return { label, value };
  });

  const topCategoriesByCount = categoriesByCountBuckets.slice(0, topN).map(b => {
    const label =
      (b.category_name && b.category_name.buckets && b.category_name.buckets[0] && b.category_name.buckets[0].key)
      || (b.parentCategory && b.parentCategory.buckets && b.parentCategory.buckets[0] && b.parentCategory.buckets[0].key)
      || String(b.key || '');

    const value =
      (b.sales_count && typeof b.sales_count.value === 'number') ? b.sales_count.value
        : (typeof b.doc_count === 'number' ? b.doc_count : 0);

    return { label, value };
  });

  const topCategoriesByProfit = categoriesByProfitBuckets.slice(0, topN).map(b => {
    const label =
      (b.category_name && b.category_name.buckets && b.category_name.buckets[0] && b.category_name.buckets[0].key)
      || (b.parentCategory && b.parentCategory.buckets && b.parentCategory.buckets[0] && b.parentCategory.buckets[0].key)
      || String(b.key || '');

    const value =
      (b.profit && typeof b.profit.value === 'number') ? b.profit.value
        : pickNumber(b, ['profit_sum', 'profit']) || 0;

    return { label, value };
  });

  const charts = {
    itemsByCount: { labels: topItemsByCount.map(i => i.label), values: topItemsByCount.map(i => i.value) },
    itemsByProfit: { labels: topItemsByProfit.map(i => i.label), values: topItemsByProfit.map(i => i.value) },
    categoriesByCount: { labels: topCategoriesByCount.map(i => i.label), values: topCategoriesByCount.map(i => i.value) },
    categoriesByProfit: { labels: topCategoriesByProfit.map(i => i.label), values: topCategoriesByProfit.map(i => i.value) }
  };

  const summary = {
    totalQuantity,
    totalValue,
    totalCost,
    totalProfit,
    totalQuantityStr: formatNumber(totalQuantity),
    totalValueStr: formatCurrency(totalValue, currency),
    totalCostStr: formatCurrency(totalCost, currency),
    totalProfitStr: formatCurrency(totalProfit, currency),
    currency
  };

  const mainColumns = getPopularProductsSalesColumns();

  return { charts, tableRows, tableRowsRaw, summary, mainColumns };
}

export default parsePopularProducts;